import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, Edit, Menu, Users, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import Sidebar from "./Sidebar";
import LanguageSwitcher from "./LanguageSwitcher";
import { API } from "../../lib/api";
import { useLang } from "../../lib/LanguageContext";

const Dashboard = () => {
  const { t, lang } = useLang();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const navigate = useNavigate();

  const fetchModels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/models`);
      setModels(response.data);
    } catch (error) {
      toast.error(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleDeleteClick = (model) => {
    setModelToDelete(model);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!modelToDelete) return;
    try {
      await axios.delete(`${API}/models/${modelToDelete.id}`);
      toast.success(t('modelDeleted'));
      fetchModels();
    } catch (error) {
      toast.error("Greška pri brisanju modela");
    }
    setDeleteDialogOpen(false);
    setModelToDelete(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="main-content">
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-8 md:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-text-secondary hover:text-white"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Sparkles className="w-7 h-7 text-primary" />
          <div className="w-6" />
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">{t('myModels')}</h1>
            <p className="text-text-secondary mt-1">{lang === 'hr' ? 'Upravljajte i kreirajte AI modele' : 'Manage and create AI models'}</p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
          <Button 
            onClick={() => navigate('/models/new')}
            className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 btn-glow"
            data-testid="dashboard-new-model-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('newModel')}
          </Button>
          </div>
        </div>

        {/* Models Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        ) : models.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-models-state">
            <Users className="w-16 h-16 mx-auto text-text-secondary mb-4" />
            <h2 className="text-xl font-bold mb-2">Nema modela</h2>
            <p className="text-text-secondary mb-6">{t('createFirst')}</p>
            <Button 
              onClick={() => navigate('/models/new')}
              className="bg-gradient-to-r from-primary to-secondary hover:opacity-90"
              data-testid="empty-state-new-model-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Kreiraj model
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="models-grid">
            {models.map((model) => (
              <div 
                key={model.id} 
                className="model-card cursor-pointer"
                onClick={() => navigate(`/models/${model.id}`)}
                data-testid={`model-card-${model.id}`}
              >
                <div className="relative">
                  {model.thumbnail ? (
                    <img 
                      src={`data:image/png;base64,${model.thumbnail}`}
                      alt={model.name}
                      className="model-card-image"
                    />
                  ) : (
                    <div className="model-card-image bg-surface-highlight flex flex-col items-center justify-center gap-2">
                      <Users className="w-12 h-12 text-text-secondary" />
                      <button
                        className="px-3 py-1.5 text-xs bg-accent/20 hover:bg-accent/30 text-accent rounded-lg transition-colors"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await axios.post(`${API}/models/${model.id}/retry-thumbnail`);
                            toast.success("Generiranje thumbnail-a pokrenuto...");
                            setTimeout(fetchModels, 10000);
                          } catch { toast.error("Greška"); }
                        }}
                        data-testid={`retry-thumb-${model.id}`}
                      >
                        Generiraj sliku
                      </button>
                    </div>
                  )}
                  {model.is_mature && (
                    <span className="mature-badge absolute top-4 right-4">18+</span>
                  )}
                  <div className="model-card-overlay">
                    <h3 className="text-lg font-bold">{model.name}</h3>
                    <p className="text-text-secondary text-sm truncate">{model.description || model.style}</p>
                  </div>
                </div>
                <div className="p-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex-1 border-white/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/models/${model.id}/edit`);
                    }}
                    data-testid={`edit-model-${model.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {lang === 'hr' ? 'Uredi' : 'Edit'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-white/10 text-error hover:text-error hover:bg-error/10"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(model);
                    }}
                    data-testid={`delete-model-${model.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteModelTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              Ova radnja će trajno obrisati model "{modelToDelete?.name}" i sve povezane slike.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10" data-testid="delete-cancel-btn">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-error hover:bg-error/90"
              data-testid="delete-confirm-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
